import { hashKey, useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { SelectedFacetParam } from "common/types";

import { parseListLeadsSearchParams } from "../utils";

import type {
  ListLead,
  ListLeadsFacets,
  ListLeadsSearchParams,
} from "../types";

interface PagingParams {
  page: number;
  pageSize: number;
}

interface ListLeadsResponse {
  people: ListLead[];
  custom_variable_keys: string[];
  total: number;
  facets: ListLeadsFacets;
}

async function searchListLeadsRequest(
  workspaceId: string,
  listId: string,
  searchParams: ListLeadsSearchParams<SelectedFacetParam>,
  { page, pageSize }: PagingParams,
): Promise<ListLeadsResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  queryParams.set("size", pageSize.toString());

  const { people, custom_variable_keys, total, facets } = await post(
    `workspaces/${workspaceId}/lists/${listId}/leads-search?${queryParams.toString()}`,
    parseListLeadsSearchParams(searchParams),
  );

  return { people, custom_variable_keys, total, facets };
}

/** The leads the list still targets, searched through the leads-search. */
export default function useIncludedListLeads({
  listId,
  searchParams,
  pagingParams,
}: {
  listId: string;
  searchParams: ListLeadsSearchParams<SelectedFacetParam>;
  pagingParams: PagingParams;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const {
    data,
    isFetching: isFetchingListLeads,
    error: listLeadsError,
    refetch: refetchListLeads,
  } = useQuery({
    queryKey: listsKeys.leads(listId, false, searchParams, pagingParams),
    queryFn: () =>
      searchListLeadsRequest(workspaceId, listId, searchParams, pagingParams),
    enabled: !!listId,
    //keeps the current page visible while the next one loads - a different
    //search returns different rows, so nothing is kept across those
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery) {
        const searchQueryKey = listsKeys.leads(listId, false, searchParams);
        if (
          hashKey(previousQuery.queryKey.slice(0, searchQueryKey.length)) ===
          hashKey(searchQueryKey)
        ) {
          return previousData;
        }
      }
    },
    gcTime: 0,
  });

  return {
    listLeads: data?.people,
    listLeadsTotal: data?.total,
    listLeadsFacets: data?.facets,
    customVariableKeys: data?.custom_variable_keys,
    isFetchingListLeads,
    listLeadsError,
    refetchListLeads,
  };
}
